import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
} from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface FAQ {
  _id: string;
  id?: string;
  question: Record<string, string>;
  answer: Record<string, string>;
  category: string;
  page: string;
  tags: string[];
  displayOrder: number;
  isActive: boolean;
}

interface FAQFormProps {
  open: boolean;
  onClose: () => void;
  faq: FAQ | null;
}

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`faq-tabpanel-${index}`}
      aria-labelledby={`faq-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Simple Rich Text Editor for Answer
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        '& .tiptap': {
          minHeight: '150px',
          padding: '12px',
          fontSize: '14px',
          outline: 'none',
          '& p.is-editor-empty:first-child::before': {
            content: `"${placeholder ?? 'Start typing...'}"`,
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
          },
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          borderBottom: '1px solid #e0e0e0',
          padding: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          backgroundColor: '#fafafa',
        }}
      >
        <Button
          size="small"
          variant={editor.isActive('bold') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <strong>B</strong>
        </Button>
        <Button
          size="small"
          variant={editor.isActive('italic') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <em>I</em>
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          List
        </Button>
        <Button
          size="small"
          variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          1.2.3
        </Button>
      </Box>
      <EditorContent editor={editor} />
    </Box>
  );
};

const categoryOptions = [
  { value: 'general', label: 'General' },
  { value: 'credits', label: 'Credits' },
  { value: 'premium', label: 'Premium' },
  { value: 'technical', label: 'Technical' },
  { value: 'account', label: 'Account' },
];

const pageOptions = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'face_swap', label: 'Face Swap (/face-swap)' },
  { value: 'multiple_face_swap', label: 'Multiple Face Swap (/multiple-face-swap)' },
  { value: 'video_face_swap', label: 'Video Face Swap (/video-face-swap)' },
  { value: 'photo_enhancer', label: 'Photo Enhancer (/photo-enhancer)' },
];

const FAQForm: React.FC<FAQFormProps> = ({ open, onClose, faq }) => {
  const [tabValue, setTabValue] = useState(0);
  const [category, setCategory] = useState('general');
  const [page, setPage] = useState('landing');
  const [isActive, setIsActive] = useState(true);
  const [tagsInput, setTagsInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [questionValues, setQuestionValues] = useState<Record<string, string>>({});
  const [answerValues, setAnswerValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const isEditing = !!faq;

  // Fetch languages
  const { data: languages, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await apiService.getLanguages();
      return response.data as Language[];
    },
  });

  const activeLanguages = languages?.filter((l) => l.isActive) || [];

  // Initialize form when faq changes
  useEffect(() => {
    if (open) {
      if (faq) {
        setCategory(faq.category || 'general');
        setPage(faq.page || 'landing');
        setIsActive(faq.isActive !== false);
        setTags(faq.tags || []);

        // Initialize multilingual values
        const initialQuestions: Record<string, string> = {};
        const initialAnswers: Record<string, string> = {};

        activeLanguages.forEach((lang) => {
          const code = lang.code.toUpperCase();
          initialQuestions[code] = faq.question?.[code] || faq.question?.[lang.code] || '';
          initialAnswers[code] = faq.answer?.[code] || faq.answer?.[lang.code] || '';
        });

        setQuestionValues(initialQuestions);
        setAnswerValues(initialAnswers);
      } else {
        // Reset form for new FAQ
        setCategory('general');
        setPage('landing');
        setIsActive(true);
        setTags([]);
        setTagsInput('');

        const initialQuestions: Record<string, string> = {};
        const initialAnswers: Record<string, string> = {};

        activeLanguages.forEach((lang) => {
          const code = lang.code.toUpperCase();
          initialQuestions[code] = '';
          initialAnswers[code] = '';
        });

        setQuestionValues(initialQuestions);
        setAnswerValues(initialAnswers);
      }
      setTabValue(0);
    }
  }, [faq, open, languages]);

  const handleAddTag = () => {
    const trimmedTag = tagsInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagsInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTranslate = async (field: 'question' | 'answer' | 'both') => {
    const englishQuestion = questionValues['EN'] || '';
    const englishAnswer = answerValues['EN'] || '';

    if (field === 'question' && !englishQuestion) {
      toast.error('Please provide English question to translate');
      return;
    }
    if (field === 'answer' && !englishAnswer) {
      toast.error('Please provide English answer to translate');
      return;
    }
    if (field === 'both' && !englishQuestion && !englishAnswer) {
      toast.error('Please provide English content to translate');
      return;
    }

    setIsTranslating(true);

    try {
      const targetLanguages = activeLanguages.filter((l) => l.code.toUpperCase() !== 'EN');

      if (targetLanguages.length === 0) {
        toast.error('No other languages to translate to');
        return;
      }

      const newQuestionValues = { ...questionValues };
      const newAnswerValues = { ...answerValues };
      let translatedCount = 0;

      for (const lang of targetLanguages) {
        const code = lang.code.toUpperCase();

        if ((field === 'question' || field === 'both') && englishQuestion && !newQuestionValues[code]) {
          try {
            const response = await apiService.translateText(englishQuestion, code, 'en');
            if (response.success && response.data?.translatedText) {
              newQuestionValues[code] = response.data.translatedText;
              translatedCount++;
            }
          } catch (err) {
            console.error(`Failed to translate question to ${code}`, err);
          }
        }

        if ((field === 'answer' || field === 'both') && englishAnswer && !newAnswerValues[code]) {
          try {
            const response = await apiService.translateHtml(englishAnswer, code, 'en');
            if (response.success && response.data?.translatedHtml) {
              newAnswerValues[code] = response.data.translatedHtml;
              translatedCount++;
            }
          } catch (err) {
            console.error(`Failed to translate answer to ${code}`, err);
          }
        }
      }

      setQuestionValues(newQuestionValues);
      setAnswerValues(newAnswerValues);

      if (translatedCount > 0) {
        toast.success(`Translated ${translatedCount} field(s) successfully`);
      } else {
        toast('No translations needed (all fields already have values)');
      }
    } catch (err: any) {
      toast.error('Translation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = async () => {
    // Validate - at least English question is required
    if (!questionValues['EN']?.trim()) {
      toast.error('English question is required');
      return;
    }

    if (!answerValues['EN']?.trim() || answerValues['EN'] === '<p></p>') {
      toast.error('English answer is required');
      return;
    }

    setSaving(true);

    try {
      // Build question and answer objects (only include non-empty values)
      const question: Record<string, string> = {};
      const answer: Record<string, string> = {};

      Object.entries(questionValues).forEach(([code, value]) => {
        if (value?.trim()) {
          question[code] = value;
        }
      });

      Object.entries(answerValues).forEach(([code, value]) => {
        if (value?.trim() && value !== '<p></p>') {
          answer[code] = value;
        }
      });

      const data = {
        question,
        answer,
        category,
        page,
        tags,
        isActive,
      };

      let response;
      if (isEditing && faq) {
        response = await apiService.updateFAQ(faq._id, data);
      } else {
        response = await apiService.createFAQ(data);
      }

      if (response.success) {
        toast.success(isEditing ? 'FAQ updated successfully' : 'FAQ created successfully');
        onClose();
      } else {
        toast.error(response.error || 'Failed to save FAQ');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
      <DialogContent sx={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <Box sx={{ pt: 2 }}>
          {/* Category, Page and Status */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flexGrow: 1, minWidth: 220 }}>
              <InputLabel>Page</InputLabel>
              <Select value={page} label="Page" onChange={(e) => setPage(e.target.value)}>
                {pageOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Active"
              sx={{ minWidth: 100 }}
            />
          </Box>

          {/* Tags */}
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Tags
            </Typography>
            <Box display="flex" gap={1} alignItems="center" mb={1}>
              <TextField
                size="small"
                placeholder="Add tag..."
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button variant="outlined" size="small" onClick={handleAddTag}>
                Add
              </Button>
            </Box>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" onDelete={() => handleRemoveTag(tag)} />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Multilingual Tabs */}
          {languagesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Question & Answer
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TranslateIcon />}
                  onClick={() => handleTranslate('both')}
                  disabled={isTranslating || (!questionValues['EN'] && !answerValues['EN'])}
                >
                  {isTranslating ? 'Translating...' : 'Translate All'}
                </Button>
              </Box>

              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {activeLanguages.map((lang, index) => (
                  <Tab
                    key={lang.code}
                    label={`${lang.name} ${lang.code.toUpperCase() === 'EN' ? '(Required)' : ''}`}
                    id={`faq-tab-${index}`}
                    aria-controls={`faq-tabpanel-${index}`}
                  />
                ))}
              </Tabs>

              {activeLanguages.map((lang, index) => {
                const code = lang.code.toUpperCase();
                return (
                  <TabPanel key={lang.code} value={tabValue} index={index}>
                    <Box mb={3}>
                      <TextField
                        fullWidth
                        label={`Question (${lang.name})`}
                        value={questionValues[code] || ''}
                        onChange={(e) =>
                          setQuestionValues({
                            ...questionValues,
                            [code]: e.target.value,
                          })
                        }
                        required={code === 'EN'}
                        multiline
                        rows={2}
                        helperText={code === 'EN' ? 'Required' : 'Optional'}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Answer ({lang.name}) {code === 'EN' ? '- Required' : '- Optional'}
                      </Typography>
                      <RichTextEditor
                        value={answerValues[code] || ''}
                        onChange={(value) =>
                          setAnswerValues({
                            ...answerValues,
                            [code]: value,
                          })
                        }
                        placeholder="Enter the answer..."
                      />
                    </Box>
                  </TabPanel>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FAQForm;
